'use client';

import { ComponentPropsWithoutRef, forwardRef, useEffect, useRef } from 'react';

import { useMergeRefs } from '@floating-ui/react';

import { EVENT_NAMES, EventValueType, trackEvent } from '@/analytics';

interface ExternalLinkProps
  extends Omit<ComponentPropsWithoutRef<'a'>, 'target' | 'rel'> {
  trackingEventName?: EventValueType;
  trackingContext: string;
}

export const ExternalLink = forwardRef<HTMLAnchorElement, ExternalLinkProps>(
  (
    {
      href,
      trackingEventName = EVENT_NAMES.external_link_click,
      trackingContext,
      ...props
    },
    ref,
  ) => {
    const thisElementRef = useRef<HTMLAnchorElement>(null);
    const combinedRefs = useMergeRefs([thisElementRef, ref]);

    useEffect(() => {
      function handleClick() {
        trackEvent(trackingEventName, {
          link_url: href,
          context: trackingContext,
        });
      }

      const element = thisElementRef.current;

      if (element) {
        element.addEventListener('click', handleClick);
      }

      return () => {
        if (element) {
          element.removeEventListener('click', handleClick);
        }
      };
    }, [href, trackingContext, trackingEventName]);

    return (
      <a
        href={href}
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        ref={combinedRefs}
      />
    );
  },
);

ExternalLink.displayName = 'ExternalLink';
